/**
 * Kwolly — Testimonials
 * Scroll-reveal for editorial quote entries + Shopify Theme Editor support.
 *
 * Uses a Web Component (custom element) following the Dawn pattern for
 * encapsulated, lifecycle-aware initialisation.
 */

(function () {
  'use strict';

  class KwollyTestimonials extends HTMLElement {
    connectedCallback() {
      this._observer = null;
      this._initReveal();
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    /**
     * Reveal quote entries as they scroll into the viewport.
     * Falls back to instant-show in the Theme Editor and when
     * the user prefers reduced motion.
     */
    _initReveal() {
      const quotes = /** @type {NodeListOf<HTMLElement>} */ (
        this.querySelectorAll('.kwt-quote--anim')
      );

      if (!quotes.length) return;

      // Theme Editor — show all immediately so merchants see full section
      if (window.Shopify && window.Shopify.designMode) {
        quotes.forEach((q) => q.classList.add('kwt-quote--visible'));
        return;
      }

      // Honour reduced-motion preference (CSS also handles this as a baseline)
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        quotes.forEach((q) => q.classList.add('kwt-quote--visible'));
        return;
      }

      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('kwt-quote--visible');
            this._observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -32px 0px',
        }
      );

      quotes.forEach((q) => this._observer.observe(q));
    }
  }

  // Register only once (guards against duplicate script loading)
  if (!customElements.get('kwolly-testimonials')) {
    customElements.define('kwolly-testimonials', KwollyTestimonials);
  }

  // ── Shopify Theme Editor events ────────────────────────────────────────

  /**
   * shopify:section:load — fired when a section is added or reloaded in the editor.
   * Re-show all quotes immediately so the full layout is visible.
   */
  document.addEventListener('shopify:section:load', (event) => {
    const component = event.target.querySelector('kwolly-testimonials');
    if (!component) return;

    component
      .querySelectorAll('.kwt-quote--anim')
      .forEach((q) => q.classList.add('kwt-quote--visible'));
  });

  /**
   * shopify:block:select — fired when a merchant clicks a block in the editor.
   * Ensure the targeted quote is revealed even if it hasn't scrolled into view.
   */
  document.addEventListener('shopify:block:select', (event) => {
    const quote = event.target.closest('.kwt-quote--anim');
    if (quote) quote.classList.add('kwt-quote--visible');
  });
})();
